import { attributes, AttributesMixin, define, Model, type } from "@type-r/models";
import { orderBy } from "lodash";
import { AssetType } from "./assetType";
import { restfulIO } from "./endpoint";
import { GoverningLaw } from "./governigLaw";
import { NotificationSubscriptionType } from "./notificationSubscription";
import { AssetTypeItem } from "./assetTypes";
import { Currency } from "./currency";
import { AssetTypeItemLookup } from "./assetTypeItemLookup";

export const OptionItemBase = attributes({
    id: type(String).null || type(Number).null,
    name: String
})

export const OptionItem = attributes({
    id: type(Number).null,
    name: String
})

export const ImportTemplateOptionItem = attributes({
    ...OptionItemBase.attributes,
    originalFileName: type(String).null,
    assetType: type(String),
    assetTypes: [AssetTypeItem],    
    assetTypeDescription: type(String),
    dataFormat: type(String),
    isDefault: type(Boolean),
    createdOn: type(Date),
    frequency: type(String),
    assetTypeId: Number
})

export interface AssetTypeLookup extends AttributesMixin<typeof AssetTypeLookup> { }

@define
export class AssetTypeLookup extends Model {
    static endpoint = restfulIO('api/v1/lookups/assettypes');

    static attributes = {
        items: [OptionItemBase]
    }

    parse(lookups: any) {
        lookups.items.unshift({ id: 0, name: "All asset types" });
        return lookups;
    }
}

export interface ImportJobStatusLookup extends AttributesMixin<typeof ImportJobStatusLookup> { }

@define
export class ImportJobStatusLookup extends Model {
    static endpoint = restfulIO('api/v1/lookups/jobstatustypes');

    static attributes = {
        items: [OptionItemBase]
    }

    parse(lookups: any) {
        lookups.items.unshift({ id: 0, name: "All statuses" });
        return lookups;
    }
}

export interface ImportTemplatesLookup extends AttributesMixin<typeof ImportTemplatesLookup> { }

@define
export class ImportTemplatesLookup extends Model {
    static endpoint = restfulIO('api/v1/lookups/importtemplates');

    static attributes = {
        items: [ImportTemplateOptionItem]
    }

    parse(lookups: any) {
        lookups.items = orderBy(lookups.items, [imp => imp.name.toLowerCase()], ['asc']);
        lookups.items.unshift({ id: null, name: "none" });
        return lookups;
    }
}

export interface CreditNoteSuppliersLookup extends AttributesMixin<typeof CreditNoteSuppliersLookup> { }

@define
export class CreditNoteSuppliersLookup extends Model {
    static endpoint = restfulIO('/api/v1/assetmaster/assetdata/suppliers/creditnote');

    static attributes = {
        items: [OptionItemBase]
    }
}

export interface CreditNoteBuyersLookup extends AttributesMixin<typeof CreditNoteBuyersLookup> { }

@define
export class CreditNoteBuyersLookup extends Model {
    static endpoint = restfulIO('/api/v1/assetmaster/assetdata/buyers/creditnote');

    static attributes = {
        items: [OptionItemBase]
    }
}

export interface PaymentPayorsLookup extends AttributesMixin<typeof PaymentPayorsLookup> { }

@define
export class PaymentPayorsLookup extends Model {
    static endpoint = restfulIO('/api/v1/assetmaster/assetdata/payment/payors');

    static attributes = {
        items: [OptionItemBase]
    }
}

export interface PaymentPayeesLookup extends AttributesMixin<typeof PaymentPayeesLookup> { }

@define
export class PaymentPayeesLookup extends Model {
    static endpoint = restfulIO('/api/v1/assetmaster/assetdata/payment/payees');

    static attributes = {
        items: [OptionItemBase]
    }
}

export interface RemittanceAdvicePayorsLookup extends AttributesMixin<typeof RemittanceAdvicePayorsLookup> { }
@define
export class RemittanceAdvicePayorsLookup extends Model {
    static endpoint = restfulIO('/api/v1/assetmaster/assetdata/remittance-advice/payors');

    static attributes = {
        items: [OptionItemBase]
    }
}

export interface RemittanceAdvicePayeesLookup extends AttributesMixin<typeof RemittanceAdvicePayeesLookup> { }
@define
export class RemittanceAdvicePayeesLookup extends Model {
    static endpoint = restfulIO('/api/v1/assetmaster/assetdata/remittance-advice/payees');

    static attributes = {
        items: [OptionItemBase]
    }
}

export interface UnappliedCashPayorsLookup extends AttributesMixin<typeof UnappliedCashPayorsLookup> { }
@define
export class UnappliedCashPayorsLookup extends Model {
    static endpoint = restfulIO('/api/v1/assetmaster/assetdata/unapplied-cash/payors');

    static attributes = {
        items: [OptionItemBase]
    }
}

export interface UnappliedCashPayeesLookup extends AttributesMixin<typeof UnappliedCashPayeesLookup> { }
@define
export class UnappliedCashPayeesLookup extends Model {
    static endpoint = restfulIO('/api/v1/assetmaster/assetdata/unapplied-cash/payees');

    static attributes = {
        items: [OptionItemBase]
    }
}

export interface ChannelType extends AttributesMixin<typeof ChannelType> { }
@define
export class ChannelType extends Model {
    static attributes = {
        name: String,
        description: String
    }
}

export interface ChannelTypes extends AttributesMixin<typeof ChannelTypes> { }

@define
export class ChannelTypes extends Model {
    static endpoint = restfulIO('api/v1/lookups/channel-types');

    static attributes = {
        items: [ChannelType]
    }

    parse(lookups: any) {
        //lookups.items.unshift({ id: '0', name: "All channel types", description: "All channel types" });
        return lookups;
    }
}

export interface FlowType extends AttributesMixin<typeof FlowType> { }

@define
export class FlowType extends Model {
    static attributes = {
        name: String,
        description: String
    }
}

export interface FlowTypes extends AttributesMixin<typeof FlowTypes> { }

@define
export class FlowTypes extends Model {
    static endpoint = restfulIO('api/v1/lookups/flow-types');

    static attributes = {
        items: [FlowType]
    }

    parse(lookups: any) {
        lookups.items.unshift({ id: '0', name: "All flow types", description: "All flow types" });
        return lookups;
    }
}

export interface PlatformsLookup extends AttributesMixin<typeof PlatformsLookup> { }

@define
export class PlatformsLookup extends Model {
    static endpoint = restfulIO('/api/v1/lookups/platforms');

    static attributes = {
        items: [OptionItemBase]
    }

    parse(lookups: any) {
        lookups.items.unshift({ id: null, name: "none" });
        return lookups;
    }
}

export const ProductOptionItem = attributes({
    ...OptionItemBase.attributes,
    description: String,
    assetTypeId: Number
})

export interface ProductsLookup extends AttributesMixin<typeof ProductsLookup> { }

@define
export class ProductsLookup extends Model {
    static endpoint = restfulIO('/api/v1/lookups/products');

    static attributes = {
        items: [ProductOptionItem]
    }

    parse(lookups: any) {
        lookups.items.unshift({ id: 0, name: '', description: 'none' });
        return lookups;
    }

    getFilteredItems(assetTypeId) {
        if (assetTypeId) {
            return this.items.filter(x => x.assetTypeId === assetTypeId);
        }

        return [];
    }
}


export interface EncryptionMethod extends AttributesMixin<typeof EncryptionMethod> { }

@define
export class EncryptionMethod extends Model {
    static attributes = {
        name: String,
        description: String
    }
}

export interface EncryptionMethodsLookup extends AttributesMixin<typeof EncryptionMethodsLookup> { }

@define
export class EncryptionMethodsLookup extends Model {
    static endpoint = restfulIO('api/v1/lookups/encryption-methods');

    static attributes = {
        items: [EncryptionMethod]
    }
}

export interface NotificationSubscriptionTypesLookup extends AttributesMixin<typeof NotificationSubscriptionTypesLookup> { }

@define
export class NotificationSubscriptionTypesLookup extends Model {
    static endpoint = restfulIO('api/v1/lookups/notificationSubscriptionTypes');

    static attributes = {
        items: [NotificationSubscriptionType]
    }
}

export interface GoverningLawsLookup extends AttributesMixin<typeof GoverningLawsLookup> { }

@define
export class GoverningLawsLookup extends Model {
    static endpoint = restfulIO('/api/v1/lookups/governing-laws');

    static attributes = {
        items: [GoverningLaw]
    }
}

export interface CurrenciesLookup extends AttributesMixin<typeof CurrenciesLookup> { }

@define
export class CurrenciesLookup extends Model {
    static endpoint = restfulIO('/api/v1/lookups/currencies');

    static attributes = {
        items: [Currency]
    }
}

export interface AssetTypesLookup extends AttributesMixin<typeof AssetTypesLookup> { }

@define
export class AssetTypesLookup extends Model {
    static endpoint = restfulIO('/api/v1/lookups/assettypes');

    static attributes = {
        items: [AssetTypeItemLookup]
    }
}

export enum StatusType {
    All = 0,
    Active = 1,
    InActive = 2
}